import { API_PREFIX } from '../lib/prefix.js';
import type { Endpoint, MockResponse, Rule, TestResult } from './types.js';

/** An `{ error: { code, message } }` body from the API, as a throwable. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(
  path: string,
  token: string,
  init: RequestInit & { body?: string } = {},
): Promise<T> {
  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${token}`,
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...init.headers,
    },
  });

  const text = await response.text();
  const payload: unknown = text === '' ? null : safeParse(text);

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `HTTP ${response.status}`,
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export interface RuleInput {
  method: string;
  pathPattern: string;
  response: MockResponse;
  enabled: boolean;
}

export const rulesApi = {
  getEndpoint: (slug: string, token: string): Promise<Endpoint> =>
    request(`/endpoints/${slug}`, token),

  updateEndpoint: (
    slug: string,
    token: string,
    patch: { name?: string | null; defaultResponse?: MockResponse },
  ): Promise<Endpoint> =>
    request(`/endpoints/${slug}`, token, { method: 'PATCH', body: JSON.stringify(patch) }),

  list: (slug: string, token: string): Promise<{ rules: Rule[] }> =>
    request(`/endpoints/${slug}/rules`, token),

  create: (slug: string, token: string, input: RuleInput): Promise<Rule> =>
    request(`/endpoints/${slug}/rules`, token, { method: 'POST', body: JSON.stringify(input) }),

  update: (slug: string, token: string, id: string, patch: Partial<RuleInput>): Promise<Rule> =>
    request(`/endpoints/${slug}/rules/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  remove: (slug: string, token: string, id: string): Promise<{ deleted: boolean }> =>
    request(`/endpoints/${slug}/rules/${id}`, token, { method: 'DELETE' }),

  reorder: (slug: string, token: string, ids: string[]): Promise<{ rules: Rule[] }> =>
    request(`/endpoints/${slug}/rules/order`, token, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    }),

  test: (
    slug: string,
    token: string,
    probe: { method: string; path: string },
  ): Promise<TestResult> =>
    request(`/endpoints/${slug}/rules/test`, token, {
      method: 'POST',
      body: JSON.stringify(probe),
    }),
};
